import { ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { Alert, Tooltip } from 'antd'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { useMemberCreatorOptions, usePageTableNavigation } from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getCreatedAtSearch, getDateRangeFormValue } from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'
import {
  botDeliveriesPersistence,
  botDeliveriesSearchSchema,
  type BotDeliveriesSearch
} from './bot-deliveries-search.ts'
import { wecomDeliveryPurpose, wecomDeliveryStatusEnum } from './const/wecom-push.ts'

const route = getRouteApi('/_authenticated/system-management/bots/$botId')
type SearchValues = Partial<BotDeliveriesSearch> & { createdAtRange?: ReturnType<typeof getDateRangeFormValue> }

export function BotDeliveries({ botId }: { botId: string }) {
  const trpc = useTRPC()
  const raw = route.useSearch()
  const navigate = route.useNavigate()
  const { currentSession } = route.useRouteContext()
  const { search, navigateSearch } = usePersistedListQuery({
    persistence: botDeliveriesPersistence,
    raw: raw.deliveriesQuery ?? botDeliveriesSearchSchema.parse({}),
    accountId: currentSession.id,
    navigate: ({ search: next, replace }) =>
      navigate({
        search: (previous) => ({
          ...previous,
          deliveriesQuery: botDeliveriesSearchSchema.parse(
            typeof next === 'function' ? next(previous.deliveriesQuery ?? {}) : next
          )
        }),
        replace
      })
  })
  const creator = useMemberCreatorOptions('bot.read', search.creatorMemberId)
  const query = useQuery({
    ...trpc.bot.wecomDeliveries.queryOptions({ ...search, botId }),
    placeholderData: keepPreviousData,
    refetchInterval: 5000
  })
  const table = usePageTableNavigation<BotDeliveriesSearch, SearchValues>({
    defaultSearch: botDeliveriesSearchSchema.parse({}),
    search,
    navigate: navigateSearch,
    showTotal: (total) => `共 ${total} 条记录`,
    getFormInitialValues: (value) => ({
      ...value,
      keyword: value.keyword ?? value.id?.toString(),
      createdAtRange: getDateRangeFormValue(value.createdFrom, value.createdTo)
    }),
    getSearchFromForm: ({ createdAtRange, ...values }) => ({
      ...values,
      id: undefined,
      ...getCreatedAtSearch(createdAtRange)
    })
  })
  type Row = NonNullable<typeof query.data>['data'][number]
  const columns: ProColumns<Row>[] = [
    { title: 'ID', dataIndex: 'id', valueType: 'digit', copyable: true, search: false, width: 80 },
    {
      title: '关键词',
      dataIndex: 'keyword',
      hideInTable: true,
      fieldProps: { placeholder: '记录 ID、客户名称、账单组号或群 ID', allowClear: true }
    },
    { title: '发送来源', dataIndex: 'purpose', valueType: 'select', valueEnum: wecomDeliveryPurpose, width: 130 },
    { title: '发送状态', dataIndex: 'status', valueType: 'select', valueEnum: wecomDeliveryStatusEnum, width: 140 },
    {
      title: '客户',
      key: 'customer',
      search: false,
      width: 180,
      render: (_, row) =>
        [...new Map(row.Groups.map(({ Group }) => [Group.Customer.id, Group.Customer.name])).values()].join('、') || '—'
    },
    {
      title: '发起时间',
      dataIndex: 'createdAt',
      valueType: 'dateTime',
      search: false,
      width: 180,
      sorter: true,
      sortOrder: search.sortOrder === 'asc' ? 'ascend' : 'descend'
    },
    {
      title: '账单组',
      key: 'groups',
      search: false,
      width: 200,
      render: (_, row) =>
        row.Groups.length ? (
          <div className="space-y-1">
            {row.Groups.map(({ groupId, revisionNumber, Group }) => (
              <div key={groupId}>
                {Group.groupNo} · 第 {revisionNumber} 版
              </div>
            ))}
          </div>
        ) : (
          '—'
        )
    },
    { title: '接收群 ID', dataIndex: 'chatId', search: false, copyable: true, ellipsis: true, width: 200 },
    {
      title: '消息类型',
      dataIndex: 'messageType',
      search: false,
      valueEnum: { pdf: 'PDF', markdown: '文字' },
      width: 90
    },
    {
      title: '发起人',
      dataIndex: 'creatorMemberId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        loading: creator.isLoading,
        options: creator.options,
        ...proRemoteSearch(creator.onSearch)
      }
    },
    { title: '发起人', dataIndex: ['creator', 'name'], search: false, width: 120 },
    {
      title: '发起日期',
      dataIndex: 'createdAtRange',
      valueType: 'dateRange',
      hideInTable: true,
      fieldProps: { presets: RANGE_PRESETS }
    },
    { title: '回执时间', dataIndex: 'acknowledgedAt', valueType: 'dateTime', search: false, width: 180 },
    {
      title: '处理信息',
      dataIndex: 'error',
      search: false,
      width: 260,
      render: (_, row) => (
        <Tooltip title={row.error}>
          <span className="line-clamp-2">{row.error || '—'}</span>
        </Tooltip>
      )
    }
  ]
  return (
    <div className="space-y-4">
      {query.error && <Alert type="error" showIcon title={query.error.message} />}
      <ProTable<Row, SearchValues>
        headerTitle="主动发送记录"
        tooltip="收录客户档案手动发送的收款账单及企微联调消息。企微已确认表示平台返回成功回执；结果未知时请先核对目标群。"
        rowKey="id"
        columns={columns}
        columnsState={{ persistenceKey: 'bot-deliveries-columns', persistenceType: 'localStorage' }}
        dataSource={query.data?.data ?? []}
        loading={query.isLoading}
        locale={{ emptyText: '暂无发送记录，请在客户档案发送收款账单，或在企微推送中发送联调消息' }}
        search={{ labelWidth: 'auto' }}
        scroll={{ x: '110rem' }}
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        onSubmit={table.onSubmit}
        onReset={table.onReset}
        // Ant Design Table 固定四参回调，extra 用于区分排序与分页。
        // eslint-disable-next-line max-params
        onChange={(_, __, sorter, extra) => {
          if (extra.action !== 'sort') return
          const selected = Array.isArray(sorter) ? sorter[0] : sorter
          void navigateSearch({
            search: {
              ...search,
              current: 1,
              sortField: 'createdAt',
              sortOrder: selected.order === 'ascend' ? 'asc' : 'desc'
            }
          })
        }}
        pagination={{ ...table.pagination, total: query.data?.total ?? 0 }}
        options={{ reload: () => query.refetch() }}
      />
    </div>
  )
}
